import {MoneyRequest} from 'pages/Portal/ClientInboxItem/types';
import {useCallback, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Sentry from 'sentry';
import getStripeLegacyCheckout, {
    StripeLegacyToken,
} from 'utils/stripe/stripe-legacy';

import {
    useGetCreditCardChargeView,
    usePostCreateCreditCardView,
} from './services';

interface UseModalWorkflowProps {
    moneyRequest: MoneyRequest;
}

const useModalWorkflow = ({moneyRequest}: UseModalWorkflowProps) => {
    const {t} = useTranslation('Portal');
    const genericErrorMessage = t(
        'clientInboxItem.payPanel.errorMessages.generic',
    );
    const timeoutErrorMessage = t(
        'clientInboxItem.payPanel.errorMessages.timeout',
    );

    const [errorMessage, setErrorMessage] = useState<string>();
    const [isPaid, setIsPaid] = useState<boolean>(moneyRequest.paid);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const pollCount = useRef<number>(0);
    const currentToken = useRef<StripeLegacyToken>();

    const postCreateCreditCardView = usePostCreateCreditCardView();
    const getCreditCardChargeView = useGetCreditCardChargeView();

    const setChargeToken = useCallback(
        (chargeMid: string) => {
            const pollFunction = async () => {
                if (!currentToken.current) {
                    setIsLoading(false);

                    return setErrorMessage(genericErrorMessage);
                }

                pollCount.current += 1;

                if (pollCount.current > 30) {
                    setIsLoading(false);

                    return setErrorMessage(timeoutErrorMessage);
                }

                getCreditCardChargeView
                    .mutateAsync({mid: chargeMid})
                    .then((response) => {
                        const {reason, state} = response?.data || {};

                        if (state === 'pending' || state === 'charging') {
                            setTimeout(pollFunction, 500);
                        }

                        if (state === 'complete') {
                            setIsPaid(true);
                        }

                        if (state === 'failed') {
                            setIsLoading(false);
                            setErrorMessage(reason);
                        }
                    })
                    .catch(() => {
                        Sentry.captureMessage('Charge created and lost! ', {
                            extra: {
                                charge_mid: chargeMid,
                                mr_token: moneyRequest.mrToken,
                            },
                        });
                    });
            };
            pollCount.current = 0;
            setTimeout(pollFunction, 500);
        },
        [
            genericErrorMessage,
            getCreditCardChargeView,
            moneyRequest.mrToken,
            timeoutErrorMessage,
        ],
    );

    const sendToken = useCallback(
        (token: StripeLegacyToken) => {
            setErrorMessage(undefined);
            currentToken.current = token;

            postCreateCreditCardView
                .mutateAsync({ccToken: token.id, mrToken: moneyRequest.mrToken})
                .then((response) => {
                    if (response) {
                        setChargeToken(response.data.mid);
                    } else {
                        setErrorMessage(genericErrorMessage);
                    }
                })
                .catch(() => setErrorMessage(genericErrorMessage));
        },
        [
            genericErrorMessage,
            moneyRequest.mrToken,
            postCreateCreditCardView,
            setChargeToken,
        ],
    );

    const onClick = useCallback(() => {
        setIsLoading(true);

        const StripeCheckout = getStripeLegacyCheckout();

        if (StripeCheckout && moneyRequest.stripeKey) {
            const stripeHandler = StripeCheckout.configure({
                closed: () => {
                    if (!currentToken.current) setIsLoading(false);
                },
                currency: moneyRequest.currency,
                description: moneyRequest.document,
                key: moneyRequest.stripeKey,
                locale: moneyRequest.langCode,
                name: moneyRequest.company,
                opened: () => setIsLoading(true),
                token: sendToken,
            });

            stripeHandler.open({
                amount: moneyRequest.totalCost,
            });
        }
    }, [moneyRequest, sendToken]);

    return {
        errorMessage,
        isLoading,
        isPaid,
        onClick,
    };
};

export default useModalWorkflow;
